import { useMutation } from '@apollo/client';
import {
  type AccountType,
  AffiliationRelations,
  AffiliationTypes,
  ApplyForGovFunds,
  FeatureFlagTypes,
  ObjectTypes,
} from '@generated/graphql';
import { type Account, isRESPAccount } from '@interfaces/account';
import type { Affiliation } from '@interfaces/affiliation';
import type {
  CustodianConnection,
  CustodianConnectionAccountTypeSettings,
} from '@interfaces/custodianConnection';
import type { CustomField } from '@interfaces/customField';
import type { TranslatedString } from '@interfaces/shared';
import CloseIcon from '@mui/icons-material/Close';
import { FormControlLabel, MenuItem, Chip as MuiChip } from '@mui/material';
import { AccountTypes, CurrencyCodes } from '@onevesthq/ov-enums';
import { Autocomplete } from '@ovComponents/2-component/autocomplete/autocomplete';
import { Badge } from '@ovComponents/2-component/badge/badge';
import { Card, CardContent } from '@ovComponents/2-component/card/card';
import { CheckboxField } from '@ovComponents/2-component/checkboxField/checkboxField';
import { Chip } from '@ovComponents/2-component/chip/chip';
import InfoDialog from '@ovComponents/2-component/infoDialog/infoDialog';
import { Radio, RadioGroup } from '@ovComponents/2-component/radio/radio';
import { SelectField } from '@ovComponents/2-component/selectField/selectField';
import { TextField } from '@ovComponents/2-component/textField/textField';
import { useCustomFieldRenderer } from '@ovComponents/3-pattern/customField/useCustomFieldRenderer';
import { RepCodeSelect } from '@ovComponents/3-pattern/repCodeSelect/repCodeSelect';
import { FieldRequiredType } from '@ovComponents/4-module/configurableOptionFields/basicField';
import { useConfirmationDialog } from '@ovComponents/4-module/workflowCompletion/components/useConfirmationDialog';
import { WorkflowCompletionForm } from '@ovComponents/4-module/workflowCompletion/components/workflowCompletionForm';
import { AnalyticsContext } from '@providers/analyticsProvider';
import { UserContext } from '@providers/userContextProvider';
import { ovAnalyticsEvents } from '@utils/analytics/analytics';
import {
  getCurrencyFlag,
  hasCurrencyFlag,
  isCurrencyCode,
} from '@utils/currency';
import { FlagsmithFlags } from '@utils/flagsmith';
import { widgetAdditionalInfo } from '@utils/widgetAdditionalInfo';
import dayjs from 'dayjs';
import { Box, Grid, Skeleton, Typography, useToast } from 'hephaestus/web';
import { flatten, isNumber, kebabCase, uniq } from 'lodash-es';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useFeatureFlag } from '../../../../../hooks/useFeatureFlag';
import { useLocalization } from '../../../../../hooks/useLocalization';
import { useOrganizationFeatureFlag } from '../../../../../hooks/useOrganizationFeatureFlag';
import { usePermission } from '../../../../../hooks/usePermission';
import { HouseholdSelection } from '../editGoal/components/householdSelection';
import { type FieldOptions, type FormErrors, validateFields } from '../utils';
import {
  type AccountConfigInterface,
  type AccountConfigSection,
  getAccountConfig,
  SectionType,
} from './accountConfig';
import { resolveAccountCurrencies } from './components/accountCurrencies';
import { Affiliate, respAccountTypes } from './components/affiliate';
import { PaymentInstruction } from './components/paymentInstruction';
import { UPDATE_ACCOUNT, type useAccountData } from './editAccount';

type ValidationResult = {
  isValid: boolean;
  invalidTypes: { type: string; total: number }[];
};

export type EditAccountSubstepOptions = {
  title: TranslatedString;
  subtitle: TranslatedString;
  showAdditionalInformation?: boolean;
  additionalInformation?: TranslatedString;
  customTitle?: TranslatedString;
  defaultLinkToHousehold: boolean;
  validateAffiliate: boolean;
  custodianSelector: boolean;
  baseCurrencySelector: boolean;
  multiCurrencySelector: boolean;
  repCode: {
    enabled: boolean;
    label: TranslatedString;
    required: string;
  };
  showOnlyLinkedAdvisorRepCodes: boolean;
  selectExistingUsers: boolean;
  createFullUsers: boolean;
  disableAddingNewAffiliates: boolean;
  enableJointAccountNewJointHolder: boolean;
  jointAccountHolderWorkflow: string;
  enableJointSingleRESPNewJointHolder: boolean;
  jointSingleRESPHolderWorkflow: string;
  enableRightOfSurvivorshipForJointCash: boolean;
  enableJointFamilyRESPNewJointHolder: boolean;
  jointFamilyRESPHolderWorkflow: string;
  enableAutoInvite: boolean;
} & { [key: string]: any };

export const EditAccountVisual = ({
  options,
  accountData,
  loading,
  loadingData,
  updateAccount,
  continueFunc,
  grid = false,
  refetch,
  skip,
  updateMode = false,
  userId,
  getCurrentJurisdictionOption,
  getJurisdictionOptions,
  getSourceOfFundsOptions,
  activeCustomFields,
  derivedCustomFields,
  custodianConnections,
  workflowCompletion,
  ignoreCustomFieldTriggerRules,
  skipEditAccount,
  isWidget = false,
  isCurrencyFieldsDisabled = false,
  currentAccountTypeData,
}: {
  options: EditAccountSubstepOptions;
  accountData: Partial<
    Account & { accountId: string | null; householdClientGroupId: string }
  >;
  loading: boolean;
  loadingData: boolean;
  grid?: boolean;
  updateAccount: ReturnType<typeof useAccountData>[1];
  continueFunc: () => void;
  refetch?: () => void;
  skip: () => void;
  updateMode?: boolean;
  userId?: string;
  filterApplicableProvinces?: any;
  getCurrentJurisdictionOption?: any;
  getJurisdictionOptions?: any;
  getSourceOfFundsOptions?: any;
  activeCustomFields?: string[];
  derivedCustomFields?: CustomField[];
  custodianConnections?: CustodianConnection[];
  workflowCompletion?: any;
  ignoreCustomFieldTriggerRules?: boolean;
  skipEditAccount?: boolean;
  isWidget?: boolean;
  isCurrencyFieldsDisabled?: boolean;
  currentAccountTypeData?: AccountType | null;
}) => {
  const { translateBackendString } = useLocalization();
  const { sendAnalytic } = useContext(AnalyticsContext);
  const { enqueueToast } = useToast();
  const { custodianConnection: defaultCustodianConnection } =
    useContext(UserContext);
  const widgetsAdditionalInfoEnabled = useFeatureFlag(
    FlagsmithFlags.widgets_additional_info,
  );
  const { t } = useTranslation(['client', 'accountsDetail', 'components']);
  const canReadCustodianConnection = usePermission('read:custodian_connection');

  const enableHousehold = useOrganizationFeatureFlag(
    FeatureFlagTypes.Household,
  );
  const enableQESI = useOrganizationFeatureFlag(
    FeatureFlagTypes.RespGrantsQesi,
  );
  const enableBCTESG = useOrganizationFeatureFlag(
    FeatureFlagTypes.RespGrantsBctesg,
  );
  const respEnhancementEnabled = useOrganizationFeatureFlag(
    FeatureFlagTypes.RespAffiliationsEnhancements,
  );

  const [skipped, setSkipped] = useState(false);
  const [edited, setEdited] = useState(false);
  const [errors, setErrors] = useState<FormErrors>(null);
  const [focused, setFocused] = useState<string[]>([]);

  const [customOptionSelected, setCustomOptionSelected] = useState<any[]>([]);
  const { renderCustomFields, validateAllCustomFields } =
    useCustomFieldRenderer({
      customOptions: customOptionSelected,
      customFieldValues: accountData.customFields,
    });
  const [requireFieldValidated, setErrorsuireFieldValidated] = useState(false);
  const [selectedCustodianConnection, setSelectedCustodianConnection] =
    useState<CustodianConnection | null>(null);

  const defaultAccountTypeSettings = {
    baseCurrency: CurrencyCodes.CAD,
    availableCurrencies: [CurrencyCodes.CAD],
    isMultiCurrencyEnabled: false,
  } as CustodianConnectionAccountTypeSettings;
  const [selectedAccountTypeSettings, setSelectedAccountTypeSettings] =
    useState<CustodianConnectionAccountTypeSettings>(
      defaultAccountTypeSettings,
    );
  const [isMultiCurrencyAccount, setIsMultiCurrencyAccount] =
    useState<boolean>(false);

  const { confirmationDialog, getUserConfirmation } = useConfirmationDialog();

  const [updateAccountData] = useMutation(UPDATE_ACCOUNT, {
    onCompleted: refetch,
  });

  const repcodeAccessEnabledInFlagsmith = useFeatureFlag(
    FlagsmithFlags.repcode_access,
  );
  const mackenzieAccountPageEnabledInFlagsmith = useFeatureFlag(
    FlagsmithFlags.mackenzie_account_page,
  );

  const showAdditionalInfo =
    widgetsAdditionalInfoEnabled &&
    widgetAdditionalInfo(translateBackendString, options);

  const accountConfig = useMemo(
    () =>
      getAccountConfig(
        {
          ...accountData,
          custodianConnection:
            selectedCustodianConnection ?? accountData.custodianConnection,
        },
        options,
        t,
        respEnhancementEnabled,
      ),
    [accountData, selectedCustodianConnection, options],
  );

  const fieldOptions: FieldOptions = useMemo(() => {
    const returnOptions = accountConfig?.fieldOptions || {};

    if (
      !canReadCustodianConnection &&
      Object.hasOwn(returnOptions, 'custodianConnection')
    ) {
      delete returnOptions.custodianConnection;
    }

    if (
      accountData.type === AccountTypes.RDSP ||
      accountData.type === AccountTypes.RDSP_JOINT
    ) {
      if (options.showRDSPTaxCreditEligibility) {
        returnOptions.eligibleForDisabilityTaxCredit = { required: true };
      }
    }

    if (options.repCode?.enabled) {
      return {
        ...returnOptions,
        repCode: {
          ...returnOptions.repCode,
          required: options?.repCode?.required === FieldRequiredType.ERROR,
        },
      };
    }

    return returnOptions;
  }, [accountConfig, options.repCode, canReadCustodianConnection]);

  const validate = useCallback(
    (candidateFields?: string[]): FormErrors => {
      const newErrors = validateFields(
        { ...fieldOptions, ...options },
        accountData,
        candidateFields,
      );
      setErrors(newErrors);
      return newErrors;
    },
    [accountData, fieldOptions],
  );

  useEffect(() => {
    validate(focused);
  }, [focused]);

  useEffect(() => {
    const customOptions: any[] = [];
    Object.keys(options).forEach((key: string) => {
      if (
        options[key]?.customField &&
        options[key].enabled &&
        activeCustomFields?.includes(key)
      ) {
        const freshField = derivedCustomFields?.find((cf) => cf.key === key);
        if (freshField) {
          customOptions.push({
            ...options[key],
            customField: {
              ...options[key].customField,
              selectOptions:
                freshField.selectOptions ??
                options[key].customField.selectOptions,
              translatedName:
                freshField.translatedName ??
                options[key].customField.translatedName,
              translatedDescription:
                freshField.translatedDescription ??
                options[key].customField.translatedDescription,
            },
          });
        } else {
          customOptions.push(options[key]);
        }
      }
    });
    setCustomOptionSelected(customOptions);
  }, [options, activeCustomFields, derivedCustomFields]);

  useEffect(() => {
    if (loadingData) return;
    const custodianConnectionToSelect =
      accountData.custodianConnection ?? defaultCustodianConnection;
    const custodianConnectionInList = custodianConnections?.find(
      (custodianConnection) =>
        custodianConnection?.id === custodianConnectionToSelect?.id,
    );
    if (custodianConnectionInList) {
      setSelectedCustodianConnection(custodianConnectionInList);
      return;
    }

    if (custodianConnectionToSelect) {
      custodianConnections?.push(custodianConnectionToSelect);
      setSelectedCustodianConnection(custodianConnectionToSelect);
    }
  }, [
    defaultCustodianConnection,
    custodianConnections,
    accountData?.custodianConnection,
    loadingData,
  ]);

  useEffect(() => {
    if (loadingData || !selectedCustodianConnection) return;

    const accountTypeSettings =
      selectedCustodianConnection.accountTypeSettings?.find(
        (settings: any) => settings.type === accountData.type,
      );
    if (!accountTypeSettings) {
      setSelectedAccountTypeSettings(defaultAccountTypeSettings);
      return;
    }

    setSelectedAccountTypeSettings(accountTypeSettings);
  }, [selectedCustodianConnection, accountData.type]);

  const validateAllocations = (accountData: {
    affiliations?: Affiliation[];
  }): ValidationResult => {
    // Filter out entries with allocation is undefined, or allocation is missing.
    const validAffiliations = accountData?.affiliations?.filter(
      (entry) =>
        entry.allocation !== undefined &&
        isNumber(entry.allocation) &&
        entry.allocation !== 0,
    );

    if (!validAffiliations) return { isValid: true, invalidTypes: [] };
    // Sum allocations per type
    const totals: Record<string, number> = {};
    validAffiliations.forEach(({ type, allocation }) => {
      totals[type] = (totals[type] || 0) + (allocation ?? 0);
    });
    // Find types where total allocation != 100, but skip if type is JOINT and total is 0
    const invalidTypes = Object.entries(totals)
      .filter(
        ([type, total]) =>
          ![AffiliationTypes.Joint, AffiliationTypes.Successor].includes(
            type as AffiliationTypes,
          ) && total !== 100,
      )
      .map(([type, total]) => ({ type, total }));

    return {
      isValid: invalidTypes.length === 0,
      invalidTypes,
    };
  };

  useEffect(() => {
    if ((accountData.availableCurrencies?.length || 0) > 1)
      setIsMultiCurrencyAccount(true);
  }, [accountData.availableCurrencies]);

  useEffect(() => {
    const { availableCurrencies, baseCurrency } =
      selectedAccountTypeSettings || {};

    if (!options.baseCurrencySelector) return;
    if (!availableCurrencies || !baseCurrency) return;

    updateAccount((prev) => ({
      ...prev,
      ...resolveAccountCurrencies({
        accountTypeSettings: selectedAccountTypeSettings,
        currentBaseCurrency: prev.baseCurrency,
        currentAvailableCurrencies: prev.availableCurrencies,
        multiCurrencySelectorEnabled: !!options.multiCurrencySelector,
      }),
    }));
  }, [
    selectedAccountTypeSettings,
    options.baseCurrencySelector,
    options.multiCurrencySelector,
  ]);

  const submit = async () => {
    const formErrors = validate();
    if (formErrors) {
      setFocused(Object.keys(formErrors));
    }
    const affiliationErrors =
      options.validateAffiliate && validateRequiredAffiliateFields();
    if (affiliationErrors) {
      setErrorsuireFieldValidated(true);
    }
    const invalidCustomFieldKeys = validateAllCustomFields();
    if (formErrors || affiliationErrors || invalidCustomFieldKeys.length > 0) {
      return;
    }
    if (!validateAllocations(accountData).isValid) {
      enqueueToast(t('shared:invalidAllocations'), { variant: 'error' });
      return;
    }
    setEdited(false);
    sendAnalytic(ovAnalyticsEvents.workflowsEditAccountContinueButtonSelect, {
      workflowStepTitle: options?.title,
      workflowId: workflowCompletion?.workflow?.id,
      workflowName: workflowCompletion?.workflow?.name,
      activeWorkflowCompletionId: workflowCompletion?.id,
      objectId: workflowCompletion?.objectId,
      objectType: workflowCompletion?.objectType,
    });

    await continueFunc();
  };

  const sections: Required<AccountConfigInterface>['sections'] = useMemo(() => {
    let secs = (accountConfig?.sections || [])
      .filter(
        (x: any) =>
          !(x.excludeProvinces || []).includes(
            accountData.user?.physicalAddress?.province,
          ),
      )
      .filter(
        (y: any) =>
          !(y.excludeMaritalStatus || []).includes(
            accountData.user?.maritalStatus,
          ),
      );

    // Hide guardian and primary caregiver sections for RESP accounts when enhancement is enabled
    if (
      respEnhancementEnabled &&
      respAccountTypes.includes(accountData.type || '')
    ) {
      secs = secs.map((section: AccountConfigSection) => {
        const isGuardianOrCaregiver =
          section.type === SectionType.AFFILIATE &&
          section.affiliateType &&
          [
            AffiliationTypes.PrimaryCaregiver,
            AffiliationTypes.Guardian,
          ].includes(section.affiliateType);

        return isGuardianOrCaregiver ? { ...section, hidden: true } : section;
      });
    }

    if (updateMode) {
      if (!options.affiliates)
        secs = secs.filter((x: any) => x.type !== SectionType.AFFILIATE);
      if (!options.respGrants)
        secs = secs.filter((x: any) => x.type !== SectionType.RESP_GRANTS);
      if (!options.rdspGrants)
        secs = secs.filter((x: any) => x.type !== SectionType.RDSP_GRANTS);
      if (!options.rrifPaymentInstructions)
        secs = secs.filter(
          (x: any) => x.type !== SectionType.RRIF_PAYMENT_INSTRUCTIONS,
        );
      if (!options.showRDSPTaxCreditEligibility)
        secs = secs.filter((x: any) => x.type !== SectionType.DTC_ELIGIBILITY);
      if (!options.guardian && mackenzieAccountPageEnabledInFlagsmith)
        secs = secs.filter((x: any) =>
          respEnhancementEnabled
            ? x.affiliateType !== AffiliationTypes.Guardian
            : x.defaultRelationType !== AffiliationRelations.Guardian,
        );
      if (!options.primaryCaregiver && mackenzieAccountPageEnabledInFlagsmith)
        secs = secs.filter((x: any) =>
          respEnhancementEnabled
            ? x.affiliateType !== AffiliationTypes.PrimaryCaregiver
            : x.defaultRelationType !== AffiliationRelations.PrimaryCaregiver,
        );
    }
    if (!options.defaultLinkToHousehold || !enableHousehold) {
      secs = secs.filter((x: any) => x.type !== SectionType.LINK_TO_HOUSEHOLD);
    }
    if (
      !options.custodianSelector ||
      !custodianConnections ||
      custodianConnections.length === 1
    ) {
      secs = secs.filter((x: any) => x.type !== SectionType.CUSTODIAN);
    }
    if (
      !options.baseCurrencySelector ||
      !selectedAccountTypeSettings?.availableCurrencies ||
      selectedAccountTypeSettings?.availableCurrencies.length === 1
    ) {
      secs = secs.filter((x: any) => x.type !== SectionType.BASE_CURRENCY);
    }
    if (
      !options.multiCurrencySelector ||
      !selectedAccountTypeSettings?.isMultiCurrencyEnabled
    ) {
      secs = secs.filter((x: any) => x.type !== SectionType.MULTI_CURRENCY);
    }
    if (options.jurisdiction === false)
      secs = secs.filter((x: any) => x.type !== SectionType.JURISDICTION);
    if (options.sourceOfFunds === false)
      secs = secs.filter((x: any) => x.type !== SectionType.SOURCE_OF_FUNDS);
    return secs;
  }, [
    accountConfig,
    accountData,
    options,
    custodianConnections,
    selectedAccountTypeSettings,
  ]);

  const organizedSections = useMemo(() => {
    const secs = sections;
    const jurisdictionAndSourceOfFunds = secs.filter((x: any) =>
      [SectionType.JURISDICTION, SectionType.SOURCE_OF_FUNDS].includes(x.type),
    );

    if (jurisdictionAndSourceOfFunds.length > 1) {
      const otherSecs = secs.filter(
        (x: any) =>
          ![SectionType.JURISDICTION, SectionType.SOURCE_OF_FUNDS].includes(
            x.type,
          ),
      );
      return [otherSecs, jurisdictionAndSourceOfFunds];
    }
    return [secs];
  }, [sections]);

  const validateRequiredAffiliateFields = () => {
    const errofields: any[] = [];
    const renderedSections = sections;
    renderedSections
      .filter(
        (section) => section.type === SectionType.AFFILIATE && section.required,
      )
      .map((sec) => {
        if (!isAffiliateAddedToTheAccount(sec)) {
          errofields.push(sec);
        }
      });
    return errofields.length !== 0;
  };

  const isAffiliateAddedToTheAccount = (section: any) => {
    let found = false;

    const affiliationData = options.useCustodianAffiliates
      ? accountData.custodianAffiliates
      : accountData.affiliations;
    affiliationData?.forEach((d: any) => {
      if (
        section.affiliateType === AffiliationTypes.Other &&
        (section?.defaultRelationType
          ? d.relation === section.defaultRelationType &&
            d.type === section.affiliateType
          : d.type === section.affiliateType)
      ) {
        found = true;
      } else if (
        d.type === section.affiliateType &&
        section.affiliateType !== AffiliationTypes.Other
      ) {
        found = true;
      }
    });
    return found;
  };

  const validateRequiredAffiliate = (section: any) => {
    if (section.type === SectionType.AFFILIATE && section.required) {
      return !isAffiliateAddedToTheAccount(section);
    }
    return false;
  };

  const affiliateData = () => {
    if (options.useCustodianAffiliates) {
      return [
        ...(accountData.custodianAffiliates || []),
        ...(accountData.affiliations || []),
      ];
    }
    return accountData.affiliations;
  };

  const isCustomFieldsSelected =
    customOptionSelected && customOptionSelected.length > 0;
  useEffect(() => {
    const shouldSkipEditAccount =
      !loadingData &&
      accountData?.accountId &&
      selectedCustodianConnection &&
      selectedAccountTypeSettings &&
      sections.length === 0 &&
      !isCustomFieldsSelected;
    if (!skipped && (shouldSkipEditAccount || skipEditAccount)) {
      setSkipped(true);
      skip();
    }
  }, [
    loadingData,
    accountData,
    accountConfig,
    selectedCustodianConnection,
    selectedAccountTypeSettings,
    skipped,
    skip,
    isCustomFieldsSelected,
  ]);

  const addToApplyForGovFunds = (grantType: ApplyForGovFunds): void => {
    if (accountData.applyForGovFunds?.includes(grantType)) return;
    updateAccount((previousData) => ({
      ...previousData,
      applyForGovFunds: [...(previousData.applyForGovFunds || []), grantType],
    }));
  };

  const removeFromApplyForGovFunds = (grantType: ApplyForGovFunds): void => {
    if (!accountData.applyForGovFunds?.includes(grantType)) return;
    updateAccount((previousData) => ({
      ...previousData,
      applyForGovFunds: previousData.applyForGovFunds?.filter(
        (x: string) => x !== grantType,
      ),
    }));
  };

  const ageInYears = (dob?: string) =>
    dayjs().utc().diff(dayjs(dob).utc(), 'year');
  const beneficiaries = useMemo(
    () =>
      (accountData.affiliations ?? []).filter(
        (x) => x.type === AffiliationTypes.PrimaryBeneficiary,
      ),
    [accountData],
  );

  const guardians = useMemo(
    () =>
      (accountData.affiliations ?? []).filter(
        (x) => x.relation === AffiliationRelations.Guardian,
      ),
    [accountData],
  );

  /* RESP: If beneficiary is <18 and address is = QC then automatically show the QESI grant ... */
  const isEligibleForQESI = useMemo(() => {
    if (!enableQESI) return false;
    if (!isRESPAccount(accountData.type)) return false;

    return beneficiaries.some((beneficiary) => {
      if (!beneficiary.user) return false;

      const beneficiaryUser = beneficiary.user;

      if (ageInYears(beneficiaryUser.dateOfBirth) >= 18) return false;

      const isQCJurisdiction =
        beneficiaryUser.physicalAddress?.jurisdiction === 'CA_QC' ||
        beneficiaryUser.physicalAddress?.province === 'QC';

      if (!isQCJurisdiction) return false;

      return true;
    });
  }, [accountData, beneficiaries]);

  /* RESP: ... and make it selected. */
  useEffect(() => {
    if (updateMode) return;
    if (enableQESI && isEligibleForQESI) {
      addToApplyForGovFunds(ApplyForGovFunds.Qesi);
    }
  }, [isEligibleForQESI]);

  /* RESP: If beneficiary age is between 6 - 9 AND both subscriber+beneficiary province is BC, then eligible for BCTESG */
  const isEligibleForBCTESG = useMemo(() => {
    if (!enableBCTESG) return false;
    if (!isRESPAccount(accountData.type)) return false;

    return beneficiaries.some((beneficiary) => {
      if (!beneficiary.user) return false;

      const beneficiaryUser = beneficiary.user;

      if (dayjs(beneficiaryUser.dateOfBirth).year() < 2006) return false;

      const beneficiaryAge = ageInYears(beneficiaryUser.dateOfBirth);
      if (beneficiaryAge < 6 || beneficiaryAge > 9) return false;

      const isBeneficiaryUnderBcJurisdiction =
        beneficiaryUser.physicalAddress?.jurisdiction === 'CA_BC' ||
        beneficiaryUser.physicalAddress?.province === 'BC';
      const isBeneficiaryChildOfSubscriber =
        beneficiary.relation === AffiliationRelations.Child;
      const isSubscriberUnderBcJurisdiction =
        accountData.user?.physicalAddress?.jurisdiction === 'CA_BC' ||
        accountData.user?.physicalAddress?.province === 'BC';
      const isGuardianUnderBcJurisdiction = guardians.some(
        (guardian) =>
          guardian.user?.physicalAddress?.jurisdiction === 'CA_BC' ||
          guardian.user?.physicalAddress?.province === 'BC',
      );

      return (
        isBeneficiaryUnderBcJurisdiction &&
        ((isBeneficiaryChildOfSubscriber && isSubscriberUnderBcJurisdiction) ||
          isGuardianUnderBcJurisdiction)
      );
    });
  }, [accountData, beneficiaries]);

  /* RESP: ... and make it selected. */
  useEffect(() => {
    if (updateMode) return;
    if (enableBCTESG && isEligibleForBCTESG) {
      addToApplyForGovFunds(ApplyForGovFunds.Bctesg);
    }
  }, [isEligibleForBCTESG]);

  const affiliateSections = organizedSections.flatMap((sec) =>
    sec.filter((s) => s.type === SectionType.AFFILIATE),
  );
  const renderSection = (
    section: (typeof organizedSections)[number][number],
  ) => {
    if (section.hidden) {
      return null;
    }
    switch (section.type) {
      case SectionType.AFFILIATE: {
        // in a case where a user can not edit after creating (edittableAfterOpen == false)
        // this allows them to create if they haven't created before.
        const hasNoAffiliates: boolean =
          !section.edittableAfterOpen &&
          affiliateData()?.filter(
            (item: { type: string }) => item.type === section.affiliateType,
          ).length === 0;
        const isAffiliatesEditable: boolean | undefined =
          !updateMode ||
          ((options.editableAffiliates as boolean) &&
            (hasNoAffiliates || section.edittableAfterOpen));
        return (
          <Affiliate
            data-testid='affiliates'
            type={section.affiliateType!}
            account={accountData}
            affiliateSections={affiliateSections}
            affiliates={
              respEnhancementEnabled
                ? ((affiliateData()?.filter(
                    (x) =>
                      x.type === section.affiliateType ||
                      ([
                        AffiliationRelations.PrimaryCaregiver,
                        AffiliationRelations.Guardian,
                      ].includes(x.relation) &&
                        (x.relation as string) ===
                          (section.affiliateType as string)),
                  ) || []) as Affiliation[])
                : ((affiliateData()?.filter(
                    (x: any) =>
                      x.type === section.affiliateType &&
                      (section.defaultRelationType
                        ? x.relation === section.defaultRelationType
                        : true),
                  ) || []) as Affiliation[])
            }
            allAffiliates={affiliateData()}
            updateAccount={updateAccount}
            multi={section.multi}
            showAllocation={section.showAllocation && !options.hideAllocation}
            showRelationship={
              section.showRelationship && !options.hideRelationship
            }
            showMissingAffiliate={!options.useCustodianAffiliates}
            fields={section.fields}
            optionalFields={section.optionalFields}
            defaultRelationType={section.defaultRelationType}
            refetch={refetch}
            updateMode={updateMode}
            edittable={isAffiliatesEditable}
            onEdit={() => setEdited(true)}
            title={section.title}
            actionTitle={section.actionTitle}
            useAccountHoldersAddress={section.useAccountHoldersAddress}
            error={requireFieldValidated && validateRequiredAffiliate(section)}
            supportText={translateBackendString(options.customSupportMessage)}
            errorText={t('shared:requireAffiliate', {
              accountType: t(`accountTypes:${accountData.type}`),
              affiliateType: t(
                `affiliationTypes:${section.affiliateType === 'OTHER' && section.defaultRelationType ? section.defaultRelationType : section.affiliateType}`,
              ),
            })}
            options={options}
            canUseExistingAffiliate={section.canUseExistingAffiliate}
            isWidget={isWidget}
          />
        );
      }
      case SectionType.RESP_GRANTS:
        return (
          <>
            <Typography variant='bodyLarge' sx={{ fontWeight: 600 }}>
              {t('accountsDetail:govGrants')}
            </Typography>
            {updateMode ? (
              <Box sx={{ mt: 1 }}>
                {accountData.applyForGovFunds?.includes(
                  ApplyForGovFunds.BasicCesg,
                ) && (
                  <Chip
                    data-testid='gov-fund-basic-cesg'
                    text={t('accountsDetail:govFundOptions.BASIC_CESG')}
                  />
                )}
                {accountData.applyForGovFunds?.includes(
                  ApplyForGovFunds.AdditionalCesg,
                ) && (
                  <Chip
                    data-testid='gov-fund-additional-cesg'
                    text={t('accountsDetail:govFundOptions.ADDITIONAL_CESG')}
                  />
                )}
                {accountData.applyForGovFunds?.includes(
                  ApplyForGovFunds.Clb,
                ) && (
                  <Chip
                    data-testid='gov-fund-clb'
                    text={t('accountsDetail:govFundOptions.CLB')}
                  />
                )}
                {accountData.applyForGovFunds?.includes(
                  ApplyForGovFunds.Qesi,
                ) &&
                  enableQESI && (
                    <Chip
                      data-testid='gov-fund-qesi'
                      text={t('accountsDetail:govFundOptions.QESI')}
                    />
                  )}
                {accountData.applyForGovFunds?.includes(
                  ApplyForGovFunds.Bctesg,
                ) &&
                  enableBCTESG && (
                    <Chip
                      data-testid='gov-fund-bctesg'
                      text={t('accountsDetail:govFundOptions.BCTESG')}
                    />
                  )}
              </Box>
            ) : (
              <Box
                sx={{
                  mt: 1,
                  mb: 2,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1,
                }}
              >
                <CheckboxField
                  data-testid='gov-fund-basic-cesg'
                  label={t('accountsDetail:govFundOptions.BASIC_CESG')}
                  checked={accountData.applyForGovFunds?.includes(
                    ApplyForGovFunds.BasicCesg,
                  )}
                  onChange={(checked: boolean) =>
                    checked
                      ? addToApplyForGovFunds(ApplyForGovFunds.BasicCesg)
                      : removeFromApplyForGovFunds(ApplyForGovFunds.BasicCesg)
                  }
                />
                <CheckboxField
                  data-testid='gov-fund-additional-cesg'
                  label={t('accountsDetail:govFundOptions.ADDITIONAL_CESG')}
                  checked={accountData.applyForGovFunds?.includes(
                    ApplyForGovFunds.AdditionalCesg,
                  )}
                  onChange={(checked: boolean) =>
                    checked
                      ? addToApplyForGovFunds(ApplyForGovFunds.AdditionalCesg)
                      : removeFromApplyForGovFunds(
                          ApplyForGovFunds.AdditionalCesg,
                        )
                  }
                />
                <CheckboxField
                  data-testid='gov-fund-clb'
                  label={t('accountsDetail:govFundOptions.CLB')}
                  checked={accountData.applyForGovFunds?.includes(
                    ApplyForGovFunds.Clb,
                  )}
                  onChange={(checked: boolean) =>
                    checked
                      ? addToApplyForGovFunds(ApplyForGovFunds.Clb)
                      : removeFromApplyForGovFunds(ApplyForGovFunds.Clb)
                  }
                />
                {enableQESI && isEligibleForQESI && (
                  <CheckboxField
                    data-testid='gov-fund-qesi'
                    label={t('accountsDetail:govFundOptions.QESI')}
                    checked={accountData.applyForGovFunds?.includes(
                      ApplyForGovFunds.Qesi,
                    )}
                    onChange={(checked: boolean) =>
                      checked
                        ? addToApplyForGovFunds(ApplyForGovFunds.Qesi)
                        : removeFromApplyForGovFunds(ApplyForGovFunds.Qesi)
                    }
                  />
                )}
                {enableBCTESG && isEligibleForBCTESG && (
                  <>
                    <CheckboxField
                      data-testid='gov-fund-bctesg'
                      label={t('accountsDetail:govFundOptions.BCTESG')}
                      checked={accountData.applyForGovFunds?.includes(
                        ApplyForGovFunds.Bctesg,
                      )}
                      onChange={(checked: boolean) =>
                        checked
                          ? addToApplyForGovFunds(ApplyForGovFunds.Bctesg)
                          : removeFromApplyForGovFunds(ApplyForGovFunds.Bctesg)
                      }
                    />
                    {accountData.type !== AccountTypes.RESP_ADULT && (
                      <Box
                        sx={{
                          my: 2,
                        }}
                      >
                        <CheckboxField
                          checkBoxStyle={{
                            alignItems: 'flex-start',
                            marginTop: '4px',
                          }}
                          data-testid='resp-all-beneficiaries-are-in-bc'
                          label={t(
                            'accountsDetail:respAllBeneficiariesAreInBc',
                          )}
                          checked={accountData.respAllBeneficiariesAreInBc}
                          onChange={(checked: boolean) =>
                            updateAccount((previous) => ({
                              ...previous,
                              respAllBeneficiariesAreInBc: checked,
                            }))
                          }
                        />
                      </Box>
                    )}
                  </>
                )}
              </Box>
            )}
          </>
        );
      case SectionType.RDSP_GRANTS:
        return (
          <>
            <Typography
              variant='labelLarge'
              component='div'
              sx={{ fontWeight: 600, mt: grid ? 0 : 3 }}
            >
              {t('accountsDetail:govGrants')}
            </Typography>
            {updateMode ? (
              <Box sx={{ mt: 1 }}>
                {accountData.applyForGovFunds?.includes(
                  ApplyForGovFunds.Cdsgb,
                ) && (
                  <Chip
                    data-testid='gov-fund-basic-CDSGB'
                    text={t('accountsDetail:govFundOptions.CDSGB')}
                  />
                )}
              </Box>
            ) : (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  mt: 1,
                  flexFlow: 'wrap',
                }}
              >
                <CheckboxField
                  data-testid='gov-fund-basic-CDSGB'
                  chip
                  label={t('accountsDetail:govFundOptions.CDSGB')}
                  checked={accountData.applyForGovFunds?.includes(
                    ApplyForGovFunds.Cdsgb,
                  )}
                  onChange={(checked: boolean) =>
                    checked
                      ? addToApplyForGovFunds(ApplyForGovFunds.Cdsgb)
                      : removeFromApplyForGovFunds(ApplyForGovFunds.Cdsgb)
                  }
                />
              </Box>
            )}
          </>
        );
      case SectionType.DTC_ELIGIBILITY:
        return (
          <Grid sx={{ mt: 1, mb: 2 }} size={12}>
            <Box sx={{ mt: grid ? 0 : 3 }}>
              <RadioGroup
                label={t('accountsDetail:eligibleForDTC')}
                value={
                  accountData.eligibleForDisabilityTaxCredit !== undefined
                    ? accountData.eligibleForDisabilityTaxCredit
                      ? 'YES'
                      : 'NO'
                    : ''
                }
                onChange={(e: any) => {
                  updateAccount({
                    ...accountData,
                    eligibleForDisabilityTaxCredit: e.target.value === 'YES',
                  });
                  setEdited(true);
                }}
                disabled={updateMode}
                error={!!errors?.eligibleForDisabilityTaxCredit}
                errorText={errors?.eligibleForDisabilityTaxCredit?.message}
              >
                <Radio
                  value='YES'
                  label={t('shared:yes')}
                  disabled={options?.showRDSPTaxCreditEligibility}
                />
                <Radio
                  value='NO'
                  label={t('shared:no')}
                  disabled={options?.showRDSPTaxCreditEligibility}
                />
              </RadioGroup>
            </Box>
          </Grid>
        );
      case SectionType.RRIF_PAYMENT_INSTRUCTIONS:
        return (
          <PaymentInstruction
            data-testid='rif-payment-instruction'
            userId={userId ?? ''}
            editable={!updateMode || options.editableAffiliates}
            account={accountData}
            refetch={refetch}
            updateMode={updateMode}
            fundTitle={section.titlePrefix}
          />
        );
      case SectionType.LINK_TO_HOUSEHOLD:
        return (
          <HouseholdSelection
            data-testid='house-hold-selection'
            data={accountData}
            updateData={updateAccount}
            objectType={ObjectTypes.Account}
          />
        );
      case SectionType.JURISDICTION:
        return (
          <Box sx={{ mt: 2 }}>
            <SelectField
              testId='jurisdiction'
              fullWidth
              label={t('workflowCompletions:updateAccount.jurisdiction')}
              value={getCurrentJurisdictionOption(
                accountData.jurisdiction ?? '',
              )}
              error={!!errors?.jurisdiction}
              errorText={errors?.jurisdiction?.message}
              disabled={updateMode && !options.editableAffiliates}
              onChange={(e: any) => {
                updateAccount({ ...accountData, jurisdiction: e.target.value });
                updateAccountData({
                  variables: {
                    input: {
                      accountId: accountData.accountId,
                      jurisdiction: e.target.value,
                    },
                  },
                });
              }}
            >
              {getJurisdictionOptions().map((j: any) => (
                <MenuItem
                  key={j.id}
                  value={j.id}
                  data-testid={`jurisdiction-option-${kebabCase(j.label)}`}
                >
                  {j.label}
                </MenuItem>
              ))}
            </SelectField>
          </Box>
        );
      case SectionType.SOURCE_OF_FUNDS:
        return (
          <Box sx={{ mt: 2, mb: 2 }}>
            <SelectField
              testId='source-of-funds'
              fullWidth
              label={t('workflowCompletions:updateAccount.sourceOfFunds')}
              disabled={updateMode && !options.editableAffiliates}
              value={accountData.sourceOfFunds ?? undefined}
              error={!!errors?.sourceOfFunds}
              errorText={errors?.sourceOfFunds?.message}
              onChange={(e: any) => {
                updateAccount({
                  ...accountData,
                  sourceOfFunds: e.target.value,
                });
                updateAccountData({
                  variables: {
                    input: {
                      accountId: accountData.accountId,
                      sourceOfFunds: e.target.value,
                    },
                  },
                });
              }}
            >
              {getSourceOfFundsOptions().map((j: any) => (
                <MenuItem
                  key={j.id}
                  value={j.id}
                  data-testid={`source-of-funds-${kebabCase(j.label)}`}
                >
                  {j.label}
                </MenuItem>
              ))}
            </SelectField>
          </Box>
        );
      case SectionType.CUSTODIAN:
        return (
          <Grid spacing={2} sx={{ mt: 2 }} size={{ xs: 12, md: 12 }}>
            <SelectField
              fullWidth
              value={selectedCustodianConnection}
              label={t('accountsDetail:custodianConnectionSelector')}
              onChange={async (event: {
                target: { value: typeof selectedCustodianConnection };
              }) => {
                const selectedCustodian = event.target.value;
                const accountTypeSettings =
                  selectedCustodian?.accountTypeSettings?.find(
                    (settings: any) => settings.type === accountData.type,
                  );

                const newCustodianAccountSettings =
                  selectedCustodian?.accountTypeSettings?.find(
                    (accountSetting) =>
                      accountSetting.type === accountData.type,
                  );
                const previousCustodianAccountSettings =
                  selectedCustodianConnection?.accountTypeSettings?.find(
                    (accountSetting) =>
                      accountSetting.type === accountData.type,
                  );
                const shouldGetUserConfirmation =
                  !!newCustodianAccountSettings?.affiliateSettings?.length ||
                  !!previousCustodianAccountSettings?.affiliateSettings?.length;

                if (shouldGetUserConfirmation) {
                  const userConfirmation = await getUserConfirmation({
                    title: t(
                      'accountsDetail:custodianChangeConfirmation.title',
                    ),
                    bodyText: t(
                      'accountsDetail:custodianChangeConfirmation.body',
                    ),
                  });
                  if (!userConfirmation.confirmed) {
                    userConfirmation.closeDialog();
                    return;
                  }
                  userConfirmation.closeDialog();
                }

                setSelectedCustodianConnection(selectedCustodian);
                setIsMultiCurrencyAccount(false);
                updateAccount({
                  ...accountData,
                  custodianConnection: selectedCustodian || undefined,
                  baseCurrency: accountTypeSettings?.baseCurrency || undefined,
                  availableCurrencies: accountTypeSettings?.baseCurrency
                    ? [accountTypeSettings?.baseCurrency]
                    : [],
                });
              }}
              onBlur={() => setFocused([...focused, 'custodianConnection'])}
              disabled={updateMode && !options.editableAffiliates}
              errorText={errors?.custodianConnection?.message}
              error={!!errors?.custodianConnection}
              testId='custodian-connection-type'
            >
              {custodianConnections?.map((custodian) => (
                <MenuItem
                  key={custodian.id}
                  value={custodian as any}
                  data-testid={`custodian-connection-${kebabCase(custodian.name)}`}
                >
                  {custodian.name} {custodian.default ? '(Default)' : ''}
                </MenuItem>
              ))}
            </SelectField>
          </Grid>
        );
      case SectionType.BASE_CURRENCY:
        return (
          <Grid spacing={2} sx={{ mt: 2 }} size={{ xs: 12, md: 12 }}>
            <SelectField
              fullWidth
              value={accountData.baseCurrency}
              label={t('accountsDetail:baseCurrencySelector')}
              onChange={(event: any) => {
                const availableCurrenciesWithoutPreviousBaseCurrency =
                  accountData.availableCurrencies?.filter(
                    (currency: any) => currency !== accountData.baseCurrency,
                  ) || [];
                const availableCurrencies = Array.from(
                  new Set([
                    ...availableCurrenciesWithoutPreviousBaseCurrency,
                    event.target.value,
                  ]),
                );

                updateAccount({
                  ...accountData,
                  baseCurrency: event.target.value,
                  availableCurrencies,
                });
              }}
              onBlur={() => setFocused([...focused, 'baseCurrency'])}
              disabled={
                (updateMode && !options.editableAffiliates) ||
                isCurrencyFieldsDisabled
              }
              errorText={errors?.baseCurrency?.message}
              error={!!errors?.baseCurrency}
              testId='base-currency'
            >
              {(
                selectedAccountTypeSettings?.availableCurrencies || [
                  accountData.baseCurrency,
                ]
              ).map((currency) => {
                if (!isCurrencyCode(currency)) return <></>;

                return (
                  <MenuItem
                    key={currency}
                    value={currency}
                    data-testid={`base-currency-${kebabCase(currency)}`}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      {currency && hasCurrencyFlag(currency) && (
                        <img
                          src={getCurrencyFlag(currency)}
                          alt={currency}
                          style={{
                            width: '20px',
                            height: '20px',
                            marginRight: '12px',
                          }}
                        />
                      )}
                      <Typography variant='bodyLarge' sx={{ fontWeight: 500 }}>
                        {currency}{' '}
                        {selectedAccountTypeSettings?.baseCurrency === currency
                          ? '(Default)'
                          : ''}
                      </Typography>
                    </Box>
                  </MenuItem>
                );
              })}
            </SelectField>
          </Grid>
        );
      case SectionType.MULTI_CURRENCY:
        return (
          <>
            <Grid spacing={4} sx={{ mt: 2 }} size={{ xs: 12, md: 12 }}>
              <FormControlLabel
                label=''
                disabled={
                  (updateMode && !options.editableAffiliates) ||
                  isCurrencyFieldsDisabled
                }
                onClick={(e) => {
                  e.preventDefault();

                  if (isCurrencyFieldsDisabled) {
                    return;
                  }

                  setIsMultiCurrencyAccount(!isMultiCurrencyAccount);

                  // We want to clear the available currencies when the checkbox is false
                  // The isMultiCurrencyAccount variable is not updated right away, so
                  // If the value is true here, it means that the user has clicked to uncheck it
                  // Thus, we clear the available currencies
                  if (isMultiCurrencyAccount && accountData.baseCurrency)
                    updateAccount({
                      ...accountData,
                      availableCurrencies: [accountData.baseCurrency],
                    });
                }}
                control={
                  <CheckboxField
                    data-testid='toggle-multi-currency'
                    label={t(
                      'accountsDetail:multiCurrencySelector.toggleMultiCurrency',
                    )}
                    infoTooltip={t(
                      'accountsDetail:multiCurrencySelector.toggleMultiCurrencyInfoTooltip',
                    )}
                    customStyle={{ paddingLeft: '12px' }}
                    checkBoxStyle={{
                      'svg.MuiSvgIcon-root': { fontSize: '20px !important' },
                    }}
                    disabled={loading}
                    labelVariant={'bodyMedium'}
                    centerInfoToolTip
                    checked={isMultiCurrencyAccount}
                    onChange={(state: boolean) => {
                      if (isCurrencyFieldsDisabled) {
                        return;
                      }

                      setIsMultiCurrencyAccount(state);
                    }}
                  />
                }
              />
            </Grid>
            {isMultiCurrencyAccount && (
              <Grid spacing={2} sx={{ mt: 1 }} size={{ xs: 12, md: 12 }}>
                <Autocomplete
                  multiple
                  value={
                    accountData.availableCurrencies?.filter(
                      (currency: CurrencyCodes) =>
                        currency !== accountData.baseCurrency,
                    ) || []
                  }
                  options={
                    selectedAccountTypeSettings?.availableCurrencies?.filter(
                      (currency: CurrencyCodes) =>
                        currency !== accountData.baseCurrency,
                    ) || []
                  }
                  disableCloseOnSelect
                  getOptionLabel={(option) => option}
                  disabled={
                    (updateMode && !options.editableAffiliates) ||
                    isCurrencyFieldsDisabled
                  }
                  testId='available-currency'
                  onBlur={() => setFocused([...focused, 'availableCurrencies'])}
                  label={''}
                  helpText={t(
                    'accountsDetail:multiCurrencySelector.availableCurrenciesHelperText',
                  )}
                  errorText={errors?.availableCurrencies?.message}
                  error={!!errors?.availableCurrencies}
                  fullWidth
                  renderOption={(
                    { key, ...optionProps }: any,
                    option: any,
                    { selected }: any,
                  ) => (
                    <Box
                      key={key}
                      {...optionProps}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        '& *MuiCheckbox-root svg.MuiSvgIcon-root': {
                          fontSize: '20px',
                        },
                      }}
                    >
                      <CheckboxField
                        size='medium'
                        customStyle={{ marginRight: 1.5 }}
                        checkBoxStyle={{
                          '& .MuiSvgIcon-root': { fontSize: 28 },
                        }}
                        checked={selected}
                      />
                      {option && hasCurrencyFlag(option) && (
                        <img
                          src={getCurrencyFlag(option as CurrencyCodes)}
                          alt={option}
                          style={{
                            width: '20px',
                            height: '20px',
                            marginRight: '12px',
                          }}
                        />
                      )}
                      <Typography variant='bodyLarge' sx={{ fontWeight: 500 }}>
                        {option}
                      </Typography>
                    </Box>
                  )}
                  renderTags={(tagValue: any, getTagProps: any) =>
                    tagValue.map((option: any, index: number) => (
                      <MuiChip
                        {...getTagProps({ index })}
                        label={
                          <Typography variant='labelLarge'>{option}</Typography>
                        }
                        size='medium'
                        deleteIcon={<CloseIcon></CloseIcon>}
                        sx={{
                          '& .MuiChip-deleteIcon': {
                            fontSize: '20px',
                            color: '#17181C',
                          },
                        }}
                        onDelete={() => {
                          const availableCurrenciesWithoutDeletedOption =
                            accountData.availableCurrencies?.filter(
                              (currency: any) => currency !== option,
                            ) || [];
                          updateAccount({
                            ...accountData,
                            availableCurrencies: Array.from(
                              new Set([
                                ...availableCurrenciesWithoutDeletedOption,
                                accountData.baseCurrency,
                              ]),
                            ) as CurrencyCodes[],
                          });
                        }}
                      />
                    ))
                  }
                  onChange={(
                    _e: React.SyntheticEvent,
                    changedCurrencies: CurrencyCodes[],
                  ) => {
                    updateAccount({
                      ...accountData,
                      availableCurrencies: Array.from(
                        new Set([
                          ...changedCurrencies,
                          accountData.baseCurrency,
                        ]),
                      ) as CurrencyCodes[],
                    });
                  }}
                />
              </Grid>
            )}
          </>
        );
      case SectionType.JOINT_OWNERSHIP_TYPE:
        if (!options?.enableRightOfSurvivorshipForJointCash) {
          return <></>;
        }
        return (
          <Grid sx={{ mt: 1, mb: 2 }} size={12}>
            <Box sx={{ mt: grid ? 0 : 3 }}>
              <RadioGroup
                label={t('accountsDetail:jointOwnershipType')}
                value={accountData?.rightOfSurvivorship ? 'YES' : 'NO'}
                onChange={(e: any) => {
                  updateAccount({
                    ...accountData,
                    rightOfSurvivorship: e.target.value === 'YES',
                  });
                  setEdited(true);
                }}
                disabled={updateMode && !options?.editableAffiliates}
              >
                <Radio
                  value='YES'
                  label={t('accountsDetail:withRightOfSurvivorship')}
                />
                <Radio
                  value='NO'
                  label={t('accountsDetail:noRightOfSurvivorship')}
                />
              </RadioGroup>
            </Box>
          </Grid>
        );
      case SectionType.GROUP_INFORMATION:
        if (!updateMode || !mackenzieAccountPageEnabledInFlagsmith)
          return <></>;
        return (
          <Grid
            container
            spacing={2}
            sx={{ mt: 1, mb: 2 }}
            key='group-information'
          >
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label={t('components:groupNumber')}
                value={accountData.groupNumber}
                onChange={(e: any) => {
                  updateAccount({
                    ...accountData,
                    groupNumber: e.target.value,
                  });
                }}
                sx={{ mb: 1 }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label={t('components:groupEmployer')}
                value={accountData.groupEmployer}
                onChange={(e: any) => {
                  updateAccount({
                    ...accountData,
                    groupEmployer: e.target.value,
                  });
                }}
              />
            </Grid>
          </Grid>
        );
      default:
        return <></>;
    }
  };

  const showAccountTypeFlag = useFeatureFlag(FlagsmithFlags.show_account_type);
  const showAccountType = showAccountTypeFlag && !isWidget;

  const accountTypeLabel = currentAccountTypeData?.translatedName
    ? translateBackendString(currentAccountTypeData?.translatedName)
    : t(`accountTypes:${accountData.type}`);

  const bctesgAttestationMissing =
    accountData.applyForGovFunds?.includes(ApplyForGovFunds.Bctesg) &&
    !accountData.respAllBeneficiariesAreInBc;

  return (
    <WorkflowCompletionForm
      onSubmit={submit}
      data-testid='edit-account-form'
      showSubmitButton={edited || options.displayUpdateButton}
      submitButtonProps={{
        label: updateMode ? t('update') : t('continue'),
        disabled: loading || skipped || bctesgAttestationMissing,
        dataTestId: `edit-account-${kebabCase(translateBackendString(options.title))}-continue-button`,
      }}
    >
      {loadingData || (skipped && !isWidget) ? (
        <Box
          sx={{
            mt: 2,
          }}
        >
          <Skeleton width='100%' height='200px' variant='rounded' />
        </Box>
      ) : sections.length === 0 && !isCustomFieldsSelected ? (
        (accountData.type !== 'PERSONAL' || isWidget) && (
          <Card>
            <CardContent sx={{ display: 'flex', justifyContent: 'center' }}>
              <Typography variant='bodyLarge'>
                {t('accountsDetail:noAffiliates')}
              </Typography>
            </CardContent>
          </Card>
        )
      ) : (
        <>
          {options?.title && (
            <Typography
              variant='displayLarge'
              sx={{ mt: 1, mb: options?.subtitle ? 0 : 2 }}
            >
              {translateBackendString(options?.title)}
            </Typography>
          )}
          {options?.subtitle && (
            <Typography variant='bodyLarge' sx={{ mb: 3 }}>
              <ReactMarkdown linkTarget='_blank' remarkPlugins={[remarkGfm]}>
                {translateBackendString(options?.subtitle)}
              </ReactMarkdown>
            </Typography>
          )}

          <Box
            sx={{
              display: 'flex',
              alignItems: 'baseline',
            }}
          >
            {options?.customTitle && (
              <Typography variant='headingSmall' sx={{ mb: 2 }}>
                {translateBackendString(options?.customTitle)}
              </Typography>
            )}
            {showAdditionalInfo && (
              <InfoDialog
                information={
                  widgetAdditionalInfo(translateBackendString, options) ?? ''
                }
              />
            )}
          </Box>
          <Grid container>
            {showAccountType && (
              <Grid
                size={{ xs: 12, md: 12 }}
                sx={{
                  mt: 2,
                  mb: 2,
                }}
              >
                <Typography variant='bodyMedium' sx={{ fontWeight: 500 }}>
                  {t('client:accountType')} &nbsp;
                  <Badge label={accountTypeLabel} color='positive' />
                </Typography>
              </Grid>
            )}
            {organizedSections.map((sec, index: number) => (
              <Grid
                container
                columnSpacing={2}
                size={12}
                key={`${sec[0]?.type}-group-${index}`}
              >
                {sec.map((section: AccountConfigSection, idx: number) => {
                  if (section.hidden) {
                    return null;
                  }
                  return (
                    <Grid
                      key={`${section.type}-${idx}`}
                      size={{
                        xs: 12,
                        md:
                          grid &&
                          (organizedSections.length > 1 ||
                            organizedSections[0].length > 1)
                            ? 6
                            : 12,
                      }}
                    >
                      {section && section?.type && renderSection(section)}
                    </Grid>
                  );
                })}
              </Grid>
            ))}
            {options?.repCode?.enabled && repcodeAccessEnabledInFlagsmith && (
              <Grid
                spacing={2}
                size={12}
                sx={{
                  mt: 2,
                  mb: 2,
                }}
              >
                {options?.showOnlyLinkedAdvisorRepCodes ? (
                  <SelectField
                    testId='rep-code'
                    value={accountData.repCode?.code ?? ''}
                    onChange={(e: any) =>
                      updateAccount({
                        ...accountData,
                        repCode: { code: e.target.value },
                      })
                    }
                    fullWidth
                    label={translateBackendString(options?.repCode?.label)}
                    error={
                      !!errors?.repCode &&
                      options?.repCode?.required !==
                        FieldRequiredType.NOT_REQUIRED
                    }
                  >
                    {uniq(
                      flatten(
                        accountData?.user?.accessibleBy?.map(
                          (x: any) => x.organizationUser.repCodes,
                        ),
                      ),
                    ).map((rep: any) => (
                      <MenuItem
                        value={rep?.code}
                      >{`${rep?.code} ${rep?.name && `- ${rep.name}`}`}</MenuItem>
                    ))}
                  </SelectField>
                ) : (
                  <RepCodeSelect
                    setRepCode={(repCode) =>
                      updateAccount({ ...accountData, repCode })
                    }
                    repCode={accountData.repCode}
                    label={translateBackendString(options?.repCode?.label)}
                    error={
                      !!errors?.repCode &&
                      options?.repCode?.required !==
                        FieldRequiredType.NOT_REQUIRED
                    }
                    fullWidth
                  />
                )}
              </Grid>
            )}
            {customOptionSelected && customOptionSelected.length > 0 && (
              <Grid spacing={2} key='custom' size={{ xs: 12, md: 12 }}>
                {renderCustomFields({
                  customData: accountData,
                  update: updateAccount,
                  grid,
                  focused,
                  setFocused,
                  loading,
                  ignoreCustomFieldTriggerRules,
                  userId: userId ?? '',
                })}
              </Grid>
            )}
          </Grid>
        </>
      )}
      {confirmationDialog}
    </WorkflowCompletionForm>
  );
};

export default EditAccountVisual;
